import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { auditFieldLabelKey, formatAuditValue } from '../shared/audit-format';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputComponent, SelectComponent } from '@beautyapp/form';
import { ServicePackageService, roundMoney } from '@beautyapp/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import {
  deriveEditState,
  fieldEnablement,
  consumedQtyFor,
  reasonRequired,
  requiresRole,
  RoleName,
  EditState,
  EditableField,
  PurchaseLike,
} from './purchase-edit-rules';
import { EditReason, EDIT_REASON_LABELS_KA } from './edit-reason';
import { PaymentMethodBlockComponent } from '../shared/payment-method-block.component';
import {
  createPaymentGroup,
  buildChargePayload,
} from '../shared/payment-channels';

interface ModalData {
  purchaseId: string;
  initialPurchase: PurchaseLike & {
    displayName: string | null;
    validUntil: string | Date | null;
    comment: string | null;
    purchaseItems: Array<{
      id: string;
      serviceId: string;
      serviceName: string;
      quantity: number;
      originalPrice: number;
      discountedPrice: number;
    }>;
    purchasedAt?: string | Date | null;
    validFrom?: string | Date | null;
    purchasePrice?: number;
    paymentMethod?: string;
    status?: string;
    createdBy?: { id: string; name: string } | null;
    payment?: {
      cash: number;
      card: number;
      bank: number;
      balance: number;
      consignation: number;
      paymentType: number | null;
    };
    // The customer who owns the package + their current account balance,
    // surfaced so the operator sees who they are refunding/charging.
    customer?: {
      id: string;
      name: string;
      phone?: string;
      balance?: number;
    } | null;
  };
  servicesCatalog: Array<{
    id: string;
    name: string;
    price: number;
    discountPrice: number;
  }>;
}

@Component({
  selector: 'beautyapp-purchase-edit-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzButtonModule,
    NzSpinModule,
    NzIconModule,
    NzDatePickerModule,
    NzSelectModule,
    NzInputModule,
    NzTagModule,
    NzToolTipModule,
    NzRadioModule,
    TranslateModule,
    InputComponent,
    SelectComponent,
    PaymentMethodBlockComponent,
  ],
  templateUrl: './purchase-edit-modal.component.html',
})
export class PurchaseEditModalComponent {
  private modalRef = inject(NzModalRef);
  private packageService = inject(ServicePackageService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);
  private data: ModalData = inject(NZ_MODAL_DATA);
  private store = inject(Store);
  private modalService = inject(NzModalService);
  private destroyRef = inject(DestroyRef);

  loading = signal(false);
  audit = signal<any[]>([]);
  historyOpen = signal(false);
  state: EditState;
  // Mirrors BE assertPriceEditAllowedForRole — true when the current user lacks
  // Administrator/Admin/Manager and the purchase is fully_used (price-edit gated).
  readonly priceRoleBlocked: boolean;
  readonly stateSignal = signal<EditState>('not_used');

  // An expired package is revived by a term that ends in the future — the BE
  // rejects anything else, so the picker offers only dates from tomorrow on.
  readonly disabledValidUntil = (date: Date): boolean => {
    if (this.state !== 'expired') return false;
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date < tomorrow;
  };
  servicesCatalog = this.data.servicesCatalog;
  readonly purchaseId = this.data.purchaseId;

  readonly info = this.data.initialPurchase;
  paymentLines(): Array<{ label: string; amount: number }> {
    const p = this.info.payment;
    if (!p) return [];
    const map: Array<[keyof typeof p, string]> = [
      ['cash', 'servicePackages.editModal.payCash'],
      ['card', 'servicePackages.editModal.payCard'],
      ['balance', 'servicePackages.editModal.payBalance'],
      ['bank', 'servicePackages.editModal.payBank'],
      ['consignation', 'servicePackages.editModal.payConsignation'],
    ];
    return map
      .filter(([k]) => Number(p[k]) > 0)
      .map(([k, label]) => ({ label, amount: Number(p[k]) }));
  }

  readonly reasonOptions: ReadonlyArray<{ value: EditReason; label: string }> =
    [
      {
        value: EditReason.WRONG_AMOUNT,
        label: EDIT_REASON_LABELS_KA[EditReason.WRONG_AMOUNT],
      },
      {
        value: EditReason.DISCOUNT_GRANTED,
        label: EDIT_REASON_LABELS_KA[EditReason.DISCOUNT_GRANTED],
      },
      {
        value: EditReason.WRONG_ITEM,
        label: EDIT_REASON_LABELS_KA[EditReason.WRONG_ITEM],
      },
      {
        value: EditReason.EXTEND_VALIDITY,
        label: EDIT_REASON_LABELS_KA[EditReason.EXTEND_VALIDITY],
      },
      {
        value: EditReason.REDUCE_QUANTITY,
        label: EDIT_REASON_LABELS_KA[EditReason.REDUCE_QUANTITY],
      },
      {
        value: EditReason.OTHER,
        label: EDIT_REASON_LABELS_KA[EditReason.OTHER],
      },
    ];

  // Expose enum for template comparisons.
  readonly EditReason = EditReason;

  readonly lifecycleLabel = computed<string>(
    () => LIFECYCLE_LABELS_KA[this.stateSignal()]
  );

  readonly lifecycleColor = computed<string>(
    () => LIFECYCLE_COLORS[this.stateSignal()]
  );

  readonly lifecycleEditableTooltip = computed<string>(() => {
    const s = this.stateSignal();
    const fields = Object.keys(FIELD_LABELS_KA) as EditableField[];
    // Assume itemConsumed = 0 for the header preview — it shows the *possible*
    // editable set in this state, ignoring per-item usage gates.
    const editable = fields.filter((f) => fieldEnablement(s, f, 0).enabled);
    if (editable.length === 0) return 'რედაქტირება შეუძლებელია';
    const prefix: string = (
      {
        not_used: 'რედაქტირებადი: ',
        partially_used: 'შესაძლო: ',
        fully_used: 'რედაქტირებადი: ',
        expired: 'რედაქტირებადი: ',
        terminal: '', // unreachable — terminal returns above
      } as Record<EditState, string>
    )[s];
    return prefix + editable.map((f) => fieldLabelKa(f)).join(', ');
  });

  form = new FormGroup({
    displayName: new FormControl<string | null>(null),
    validUntil: new FormControl<Date | string | null>(
      null,
      Validators.required
    ),
    comment: new FormControl<string | null>(null),
    reason: new FormControl<EditReason | null>(null),
    reasonText: new FormControl<string | null>(null),
    // Refund settlement: credit the customer's balance, or NONE (record the
    // change, move no money). Cash refunds at the till were removed.
    settlementMethod: new FormControl<'BALANCE' | 'NONE'>('BALANCE'),
    // Charge settlement: collect the extra payment, or NONE (record only).
    chargeSettlement: new FormControl<'COLLECT' | 'NONE'>('COLLECT'),
    // Free-form note on the refund/charge (only sent when money moves).
    settlementComment: new FormControl<string | null>(null),
    items: new FormArray<FormGroup>([]),
    payment: createPaymentGroup(),
  });

  private readonly formSnapshot = toSignal(this.form.valueChanges, {
    initialValue: this.form.getRawValue(),
  });

  readonly reasonRequiredNow = computed<boolean>(() => {
    this.formSnapshot(); // subscribe
    const state = this.stateSignal();
    if (state === 'not_used' || state === 'expired' || state === 'terminal')
      return false;

    const raw = this.form.getRawValue();
    const init = this.data.initialPurchase;

    // Purchase-level field diffs.
    const purchaseFields: Array<{
      key: 'displayName' | 'validUntil' | 'comment';
      cur: unknown;
      old: unknown;
    }> = [
      {
        key: 'displayName',
        cur: raw.displayName ?? null,
        old: init.displayName ?? null,
      },
      {
        key: 'validUntil',
        cur: this.normalizeDate(raw.validUntil),
        old: this.normalizeDate(init.validUntil),
      },
      { key: 'comment', cur: raw.comment ?? null, old: init.comment ?? null },
    ];
    for (const f of purchaseFields) {
      if (f.cur !== f.old && reasonRequired(f.key, state)) return true;
    }

    // Item-level field diffs (only existing items — new items handled via 'addItem' check below).
    type InitItem = ModalData['initialPurchase']['purchaseItems'][number];
    const initItemsById = new Map<string, InitItem>(
      (init.purchaseItems as InitItem[]).map((i) => [i.id, i])
    );
    const curItems = (raw.items ?? []) as Array<{
      id?: string | null;
      serviceId?: string;
      quantity?: number;
      originalPrice?: number;
      discountedPrice?: number;
    }>;
    let hasNewItem = false;
    const seenIds = new Set<string>();
    for (const ci of curItems) {
      if (!ci.id) {
        hasNewItem = true;
        continue;
      }
      seenIds.add(ci.id);
      const initItem = initItemsById.get(ci.id);
      if (!initItem) continue;
      if (
        Number(ci.quantity) !== Number(initItem.quantity) &&
        reasonRequired('quantity', state)
      )
        return true;
      if (
        ci.serviceId !== initItem.serviceId &&
        reasonRequired('serviceId', state)
      )
        return true;
      if (
        Number(ci.originalPrice) !== Number(initItem.originalPrice) &&
        reasonRequired('originalPrice', state)
      )
        return true;
      if (
        Number(ci.discountedPrice) !== Number(initItem.discountedPrice) &&
        reasonRequired('discountedPrice', state)
      )
        return true;
    }
    // Detect removed items.
    for (const initItem of init.purchaseItems) {
      if (!seenIds.has(initItem.id) && reasonRequired('removeItem', state))
        return true;
    }
    if (hasNewItem && reasonRequired('addItem', state)) return true;

    return false;
  });

  readonly totals = computed(() => {
    this.formSnapshot();
    const items = (this.form.getRawValue().items ?? []) as Array<{
      serviceId?: string;
      quantity?: number;
      originalPrice?: number;
      discountedPrice?: number;
    }>;
    let count = 0,
      quantity = 0,
      original = 0,
      discounted = 0;
    for (const it of items) {
      // beaut-301 #6: a freshly-added row has no service yet — don't let it
      // inflate the summary counts (სერვისები / სულ რაოდენობა) before it's filled.
      if (!it.serviceId) continue;
      const q = Number(it.quantity ?? 0);
      count += 1;
      quantity += q;
      original += Number(it.originalPrice ?? 0) * q;
      // No-discount line (discountedPrice 0) → pays the original price.
      const effDisc =
        Number(it.discountedPrice) > 0
          ? Number(it.discountedPrice)
          : Number(it.originalPrice ?? 0);
      discounted += effDisc * q;
    }
    return { count, quantity, original, discounted };
  });

  readonly adjustmentPreview = computed<{
    amount: number;
    sign: '+' | '-';
  } | null>(() => {
    this.formSnapshot();
    const state = this.stateSignal();
    if (state === 'expired' || state === 'terminal') return null;
    const raw = this.form.getRawValue();
    const init = this.data.initialPurchase;
    type InitItem = ModalData['initialPurchase']['purchaseItems'][number];
    // Effective price: the discounted price when a real discount exists,
    // otherwise the original (a no-discount line has discountedPrice = 0). Keeps
    // the charge/payment preview correct when a service is added at its original
    // price — matches the BE editor + computePackagePrice rule.
    const eff = (disc: unknown, orig: unknown): number => {
      const d = Number(disc);
      return d > 0 ? d : Number(orig);
    };
    // Total-diff: settle the difference between the full money value of the line
    // items before vs after (Σ effectivePrice × quantity). One subtraction covers
    // price changes, quantity changes, added and removed items uniformly, and
    // reprices the whole line — including already-consumed units. This mirrors the
    // BE editor exactly, so the preview always matches what gets settled.
    const oldTotal = (init.purchaseItems as InitItem[]).reduce(
      (sum, i) =>
        sum + eff(i.discountedPrice, i.originalPrice) * Number(i.quantity),
      0
    );
    const newTotal = (
      (raw.items ?? []) as Array<{
        quantity?: number;
        originalPrice?: number;
        discountedPrice?: number;
      }>
    ).reduce(
      (sum, ci) =>
        sum +
        eff(ci.discountedPrice, ci.originalPrice) * Number(ci.quantity ?? 0),
      0
    );
    // Round the delta to a clean 2dp money value: kills the float drift that
    // surfaced raw amounts like ₾9.000000000000002 in the payment block, and
    // collapses a net-zero change (whose float residue is ~1e-14) back to
    // exactly 0 so it correctly yields no settlement. (beaut-293)
    const totalDiff = roundMoney(oldTotal - newTotal);
    if (totalDiff === 0) return null;
    return { amount: Math.abs(totalDiff), sign: totalDiff > 0 ? '+' : '-' };
  });

  // True when the current settlement actually moves money: a collected charge
  // or a balance refund. NONE (either direction) moves nothing, so the comment
  // box is hidden — there is no finance operation to attach the note to.
  readonly settlementMovesMoney = computed<boolean>(() => {
    this.formSnapshot();
    const adj = this.adjustmentPreview();
    if (!adj) return false;
    return adj.sign === '-'
      ? this.form.controls.chargeSettlement.value === 'COLLECT'
      : this.form.controls.settlementMethod.value === 'BALANCE';
  });

  constructor() {
    this.state = deriveEditState(this.data.initialPurchase);
    this.stateSignal.set(this.state);
    this.form.patchValue({
      displayName: this.data.initialPurchase.displayName,
      validUntil: this.data.initialPurchase.validUntil
        ? new Date(this.data.initialPurchase.validUntil)
        : null,
      comment: this.data.initialPurchase.comment,
    });
    for (const item of this.data.initialPurchase.purchaseItems) {
      this.form.controls.items.push(this.makeItemGroup(item));
    }
    // Mirrors BE assertPriceEditAllowedForRole: gate price edits on fully_used
    // to Administrator/Admin/Manager only.
    const roles = (this.store.selectSnapshot(AuthState.getRoles) ??
      []) as string[];
    const required = requiresRole('originalPrice', this.state);
    this.priceRoleBlocked =
      required.length > 0 &&
      !roles.some((r) => required.includes(r as RoleName));
    this.applyFieldEnablement();
    this.loadAudit();
  }

  private normalizeDate(v: unknown): string | null {
    if (!v) return null;
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === 'string') return v.slice(0, 10);
    return null;
  }

  private makeItemGroup(
    item: ModalData['initialPurchase']['purchaseItems'][number]
  ) {
    const group = new FormGroup({
      id: new FormControl<string | null>(item.id),
      serviceId: new FormControl<string>(item.serviceId, Validators.required),
      quantity: new FormControl<number>(item.quantity, [
        Validators.required,
        Validators.min(1),
      ]),
      originalPrice: new FormControl<number>(item.originalPrice, [
        Validators.required,
        Validators.min(0),
      ]),
      discountedPrice: new FormControl<number>(item.discountedPrice, [
        Validators.required,
        Validators.min(0),
      ]),
    });
    // beaut-288 (bug 2): whenever the service genuinely changes, the previous
    // service's price/discount/quantity no longer apply — recompute the
    // dependent fields from the newly selected service (never leave stale
    // data), and reset them when the service is cleared via the "X".
    //
    // Guard against re-emissions of the SAME id (the select can echo the
    // initial value back through the CVA on init): only react when the id
    // actually differs from the last one, so a loaded purchase keeps its saved
    // quantity/price and only a user-driven change resets the row.
    let prevServiceId: string | null = item.serviceId;
    group.controls.serviceId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((serviceId) => {
        if (serviceId === prevServiceId) return;
        prevServiceId = serviceId;
        const svc = this.servicesCatalog.find((s) => s.id === serviceId);
        group.controls.quantity.setValue(1);
        group.controls.originalPrice.setValue(svc ? svc.price : 0);
        group.controls.discountedPrice.setValue(svc ? svc.discountPrice : 0);
      });
    return group;
  }

  private consumedForGroup(group: FormGroup): number {
    const id = group.get('id')?.value;
    if (!id) return 0; // new (unsaved) items: never consumed
    return consumedQtyFor(this.data.initialPurchase, id);
  }

  private applyFieldEnablement(): void {
    if (!fieldEnablement(this.state, 'displayName').enabled)
      this.form.controls.displayName.disable();
    if (!fieldEnablement(this.state, 'validUntil').enabled)
      this.form.controls.validUntil.disable();
    if (!fieldEnablement(this.state, 'comment').enabled)
      this.form.controls.comment.disable();

    for (let i = 0; i < this.form.controls.items.length; i++) {
      const group = this.form.controls.items.at(i) as FormGroup;
      this.applyItemEnablement(group);
    }
  }

  private applyItemEnablement(group: FormGroup): void {
    const consumed = this.consumedForGroup(group);
    if (!fieldEnablement(this.state, 'quantity', consumed).enabled)
      group.get('quantity')?.disable();
    if (!fieldEnablement(this.state, 'serviceId', consumed).enabled)
      group.get('serviceId')?.disable();
    if (!fieldEnablement(this.state, 'originalPrice', consumed).enabled)
      group.get('originalPrice')?.disable();
    if (!fieldEnablement(this.state, 'discountedPrice', consumed).enabled)
      group.get('discountedPrice')?.disable();
    // Role gate: on fully_used, price is matrix-enabled but may be blocked by
    // role. Mirrors BE assertPriceEditAllowedForRole.
    if (this.priceRoleBlocked) {
      group.get('originalPrice')?.disable();
      group.get('discountedPrice')?.disable();
    }
  }

  priceWarning(itemIndex: number): string | null {
    const group = this.form.controls.items.at(itemIndex) as
      | FormGroup
      | undefined;
    if (!group) return null;
    // New (unsaved) lines haven't been paid for, so a price change can't move
    // any balance — don't show the "customer already paid" warning on them.
    if (!group.get('id')?.value) return null;
    const consumed = this.consumedForGroup(group);
    return (
      fieldEnablement(this.state, 'originalPrice', consumed).warning ?? null
    );
  }

  itemConsumed(itemIndex: number): number {
    const group = this.form.controls.items.at(itemIndex) as
      | FormGroup
      | undefined;
    return group ? this.consumedForGroup(group) : 0;
  }

  remainingFor(idx: number): number {
    const group = this.form.controls.items.at(idx) as FormGroup;
    const qty = Number(group.controls['quantity'].value ?? 0);
    return Math.max(0, qty - this.itemConsumed(idx));
  }

  removeItem(itemIndex: number): void {
    const group = this.form.controls.items.at(itemIndex) as
      | FormGroup
      | undefined;
    const consumed = group ? this.consumedForGroup(group) : 0;
    const fe = fieldEnablement(this.state, 'removeItem', consumed);
    if (!fe.enabled) {
      this.message.error(fe.reason ?? 'ეს სერვისი ვერ წაიშლება');
      return;
    }
    this.form.controls.items.removeAt(itemIndex);
  }

  addItem(): void {
    const fe = fieldEnablement(this.state, 'addItem');
    if (!fe.enabled) {
      this.message.error(fe.reason ?? 'ახალი სერვისი ვერ დაემატება');
      return;
    }
    const newGroup = this.makeItemGroup({
      id: '',
      serviceId: '',
      serviceName: '',
      quantity: 1,
      originalPrice: 0,
      discountedPrice: 0,
    });
    this.form.controls.items.push(newGroup);
    this.applyItemEnablement(newGroup);
  }

  private loadAudit(): void {
    this.packageService.getPurchaseAudit(this.data.purchaseId).subscribe({
      next: (rows: any[]) => this.audit.set(rows ?? []),
      error: (err) => {
        console.warn('[PurchaseEditModal] Failed to load audit history', err);
        this.audit.set([]);
      },
    });
  }

  /** Expose pure util as a class property so the template can call it directly. */
  readonly auditFieldLabelKey = auditFieldLabelKey;
  readonly formatAuditValue = (field: string, value: unknown) =>
    formatAuditValue(
      field,
      value,
      this.servicesCatalog.map((s) => ({
        serviceId: s.id,
        serviceName: s.name,
      }))
    );

  cancel(): void {
    this.modalRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((c) => c.markAsTouched());
      return;
    }
    if (this.reasonRequiredNow() && !this.form.controls.reason.value) {
      this.message.error('გთხოვთ აირჩიოთ რედაქტირების მიზეზი');
      return;
    }
    if (
      this.form.controls.reason.value === EditReason.OTHER &&
      !this.form.controls.reasonText.value?.trim()
    ) {
      this.message.error('მიზეზის ტექსტი სავალდებულოა, როცა არჩეულია „სხვა"');
      return;
    }
    this.persist();
  }

  private persist(confirmFutureBookingConflict = false): void {
    const raw = this.form.getRawValue();
    const validUntil =
      raw.validUntil instanceof Date
        ? raw.validUntil.toISOString()
        : raw.validUntil ?? undefined;
    const reasonRequired = this.reasonRequiredNow();
    const preview = this.adjustmentPreview();
    const charge = preview?.sign === '-';
    const refund = preview?.sign === '+';
    // NONE settlement (charge side): skip the money movement.
    const chargeNone =
      charge && this.form.controls.chargeSettlement.value === 'NONE';
    const payload: any = {
      displayName: raw.displayName ?? undefined,
      validUntil,
      comment: raw.comment ?? undefined,
      reason: reasonRequired ? raw.reason ?? undefined : undefined,
      reasonText:
        reasonRequired && raw.reasonText?.trim()
          ? raw.reasonText.trim()
          : undefined,
      // beaut-301 (bug 6): a blank "add service" row can slip past form.invalid
      // when applyItemEnablement disables its controls (disabled controls are
      // excluded from validity), which would persist an empty service line.
      // Drop any row without a serviceId before serializing.
      items: (raw.items ?? [])
        .filter((it: any) => it.serviceId)
        .map((it: any) => ({
          ...(it.id ? { id: it.id } : {}),
          serviceId: it.serviceId,
          quantity: Number(it.quantity),
          originalPrice: Number(it.originalPrice),
          discountedPrice: Number(it.discountedPrice),
        })),
      // Refund → BALANCE | NONE. Charge → NONE when the operator skips payment,
      // otherwise omitted (the BE settles the charge from the payment channels).
      settlementMethod: refund
        ? this.form.controls.settlementMethod.value ?? 'BALANCE'
        : chargeNone
        ? 'NONE'
        : undefined,
      // Only send payment channels for a charge the operator chose to collect.
      ...(charge && !chargeNone
        ? buildChargePayload(this.form.controls.payment)
        : {}),
      // Note on the refund/charge — only when money moves (it lands on the
      // finance operation's description; NONE writes no operation).
      settlementComment:
        this.settlementMovesMoney() &&
        this.form.controls.settlementComment.value?.trim()
          ? this.form.controls.settlementComment.value.trim()
          : undefined,
    };
    if (confirmFutureBookingConflict) {
      payload.confirmFutureBookingConflict = true;
    }
    this.loading.set(true);
    this.packageService
      .updatePurchase(this.data.purchaseId, payload)
      .subscribe({
        next: (purchase) => {
          this.loading.set(false);
          this.modalRef.close({ success: true, purchase });
        },
        error: (err) => {
          this.handleSaveError(err);
        },
      });
  }

  private handleSaveError(err: any): void {
    if (err?.error?.code === 'FUTURE_BOOKING_CONFLICT') {
      const dates = (err.error.bookings ?? [])
        .map(
          (b: { id: string; date: string }) =>
            this.normalizeDate(b.date) ?? String(b.date).slice(0, 10)
        )
        .join(', ');
      const datePart = dates ? ': ' + dates : '';
      const content =
        (err.error.message ? err.error.message + ' ' : '') +
        this.translate.instant(
          'servicePackages.editModal.futureBookingConflictBookings'
        ) +
        datePart;
      this.modalService.confirm({
        nzTitle: this.translate.instant(
          'servicePackages.editModal.futureBookingConflictTitle'
        ),
        nzContent: content,
        nzOkText: this.translate.instant(
          'servicePackages.editModal.futureBookingConflictConfirm'
        ),
        nzCancelText: this.translate.instant('buttons.cancel'),
        nzOnOk: () => this.persist(true),
        nzOnCancel: () => this.loading.set(false),
      });
    } else {
      this.loading.set(false);
      this.message.error(
        err.error?.message ||
          this.translate.instant('servicePackages.messages.editError')
      );
    }
  }
}

const FIELD_LABELS_KA: Record<EditableField, string> = {
  displayName: 'სახელი',
  validUntil: 'ვადა',
  comment: 'კომენტარი',
  quantity: 'რაოდენობა',
  serviceId: 'სერვისი',
  originalPrice: 'ფასი',
  discountedPrice: 'ფასდაკლება',
  addItem: 'სერვისის დამატება',
  removeItem: 'სერვისის წაშლა',
};

const LIFECYCLE_LABELS_KA: Record<EditState, string> = {
  not_used: 'ახალი',
  partially_used: 'ნაწილობრივ',
  fully_used: 'სრულად',
  expired: 'ვადაგასული',
  terminal: 'გაუქმებული',
};

const LIFECYCLE_COLORS: Record<EditState, string> = {
  not_used: 'default',
  partially_used: 'blue',
  fully_used: 'green',
  expired: 'orange',
  terminal: 'red',
};

function fieldLabelKa(f: EditableField): string {
  return FIELD_LABELS_KA[f];
}
